import { Dispatch, SetStateAction } from 'react';
import { DeviceInfo } from '@capacitor/device';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from 'react-hook-form';
import { OrganizationRole, UserPlanType } from 'features/users/users.types';
import {
  EvacZoneStatuses,
  EvacZoneStyles,
  ExternalSource,
  LinkTypes,
  ReleaseFlagKey,
} from '../constants';

// we store this
export type WDDeviceInfoStored = DeviceInfo & { uuid: string };
// and compute a few more things on the way out
export type WDDeviceInfo = WDDeviceInfoStored & {
  isMobile: boolean;
  isWeb: boolean;
  isMobilePlatform: boolean;
};

export type WDAppInfo = {
  appId: string;
  appName: string;
  appBuild: string;
  appVersion: string;
};

export type Region = {
  dateCreated: string;
  dateModified: string;
  displayName: string;
  id: number;
  isActive: boolean;
  isReporterCoveredForFire: boolean;
  name: string;
  slug: string;
  state: string;
  stateDisplayName: string;
  evacZoneStyle: RegionEvacZoneStyle | null;
};

export type EvacZoneSourceType = 'zonehaven' | 'arcgis';

export type ReleaseFlag = {
  flagKey: ReleaseFlagKey;
  flagEnabled: boolean;
};

type EvacZoneStyleKeys = keyof typeof EvacZoneStyles;

export type RegionEvacZoneStyle = (typeof EvacZoneStyles)[EvacZoneStyleKeys];

type EvacZoneExternalStatus = {
  raw?: string;
  mapped?: EvacZoneStatus;
};

export type EvacZone = {
  id: number;
  uidV2: string;
  displayName: string;
  region: {
    id: number;
    name: string;
    displayName: string;
    evacZoneStyle: RegionEvacZoneStyle;
  };
  sourceType: EvacZoneSourceType;
  sourceIdentifier: string;
  isActive?: boolean;
  isPendingReview?: boolean;
  parentUids?: string[];
  childUids?: string[];
  pendingDisplayName?: string | null; // rename held for review: the name the zone takes on release
  status?: EvacZoneStatus; // current status of the zone
  dateModified?: string; // date when the zone was last modified
  externalStatus?: EvacZoneExternalStatus; // status from external sources
  isLocked?: boolean; // if the zone is locked for manual updates, default behavior is false
};

type EvacZoneStatusKeys = keyof typeof EvacZoneStatuses;

export type EvacZoneStatus = (typeof EvacZoneStatuses)[EvacZoneStatusKeys];

export type GeoEventEvacZone = {
  id: number;
  uidV2: string;
  regionId: number;
  displayName: string;
  sourceType: EvacZoneSourceType;
  regionEvacZoneStyle: RegionEvacZoneStyle;
  isPendingReview?: boolean;
};

export type GeoEventEvacZoneStatus = {
  geoEventId: number;
  status: EvacZoneStatus;
  evacZone: GeoEventEvacZone;
  dateCreated: string;
  dateModified: string;
};

export type LinkType = (typeof LinkTypes)[keyof typeof LinkTypes];

export type Link = {
  linkType: LinkType | null;
  label: string;
  value: string;
};

type GenericExternalGeoEventData = {
  externalId: string;
  lat: number;
  lng: number;
  name: string;
  isPrescribed: boolean;
  acreage?: number;
  containment?: number;
  address?: string;
  county?: string;
  sourceUrl?: string;
  sourceLabel?: string;
  dateStart?: string;
};

export type PulsePointData = GenericExternalGeoEventData & {
  externalSource: 'pulsepoint';
  agencyId: string;
  agencyName: string;
  typeCode: string;
  unitCount: number;
  unitCountsGrouped?: Record<string, number>;
  unitsGroupedByStatus?: Record<string, string>;
  isClosed?: boolean;
};

export type AircraftDetectionData = GenericExternalGeoEventData & {
  externalSource: 'aircraft_detection';
  aircraftName: string;
  isFiris: boolean;
};

export type AlertWestAiHitData = GenericExternalGeoEventData & {
  externalSource: 'alert_west';
  hitAz: number;
  hitConfidence: number;
};

export type AlertWestDetectionData = GenericExternalGeoEventData & {
  externalSource: 'alert_west_detection';
  cameraAppUrl?: string;
  cameraId: string;
  cameraName?: string;
  detailType: string;
  detectionConfidence: number;
};

export type ArkansasForestryData = GenericExternalGeoEventData & {
  externalSource: 'arkansas_forestry';
  availableFuels: string;
  firingTechnique: string;
  responsibleParty: string;
};

export type CalFireData = GenericExternalGeoEventData & {
  externalSource: 'cal_fire';
  adminUnit: string;
};

export type ChpData = GenericExternalGeoEventData & {
  externalSource: 'chp';
  area: string;
  dispatchCenter: string;
  incidentType: string;
};

export type DnrData = GenericExternalGeoEventData & {
  externalSource: 'dnr';
  agency: string;
  burnType: string;
};

export type FireGuardData = GenericExternalGeoEventData & {
  externalSource: 'fire_guard';
  analystComments: string;
  temperature: number;
};

type FiresponseSource =
  | 'firesponse_nc'
  | 'firesponse_ga'
  | 'firesponse_fl'
  | 'firesponse_ky'
  | 'firesponse_sc'
  | 'firesponse_tn'
  | 'firesponse_va';

export type FiresponseData = GenericExternalGeoEventData & {
  externalSource: FiresponseSource;
  category: string;
};

export type FloridaForestServiceData = GenericExternalGeoEventData & {
  externalSource: 'florida_forest_service';
  burnType: string;
};

export type MfcData = GenericExternalGeoEventData & {
  externalSource: 'mfc';
  burnPurpose: string;
  burnType: string;
  dateEnd: string;
};

export type N5ShieldData = GenericExternalGeoEventData & {
  externalSource: 'n5_shield';
  description: string;
};

export type NgfsData = GenericExternalGeoEventData & {
  externalSource: 'ngfs';
  dateCreated: string;
  eventAge: string;
  frp: number;
  imageSourceLink: string;
  importance: number;
  incidentCategory: string;
  relatedIncidentLink: string;
};

export type NwsAlertData = GenericExternalGeoEventData & {
  externalSource: 'nws_alert';
  affected: string;
  certainty: string;
  damageThreat: string;
  dateEnd: string;
  description: string;
  event: string;
  instruction: string;
  isActive: boolean;
  severity: string;
  urgency: string;
};

export type NwsLsrData = GenericExternalGeoEventData & {
  externalSource: 'nws_lsr';
  city: string;
  remark: string;
  source: string;
  typetext: string;
  wfo: string;
};

export type OdfData = GenericExternalGeoEventData & {
  externalSource: 'odf';
  burnType: string;
  ownership: string;
};

export type PfirsData = GenericExternalGeoEventData & {
  externalSource: 'pfirs';
  burnType: string;
  managingAgency: string;
  status: string;
};

export type RiverGaugeData = GenericExternalGeoEventData & {
  externalSource: 'noaa_river_gauge';
  forecastFloodCategory: string | null;
  forecastFlow: number | null;
  forecastLevel: number | null;
  forecastTimestamp: string | null;
  noaaLid: string;
  observedFloodCategory: string | null;
  observedFlow: number | null;
  observedLevel: number | null;
};

type SpotForecastData = GenericExternalGeoEventData & {
  externalSource: 'spot_forecast';
  localStr: string; // Oddly named date field we get from BE
  status: string;
};

type SynopticObservation = {
  intensityInHr: number;
  rawValue: number;
  timestamp: number;
};

export type SynopticData = GenericExternalGeoEventData & {
  externalSource: 'synoptic_precipitation';
  intervalMinutes: number;
  observations: SynopticObservation[];
  stationStid: string;
  threshold: number;
};

export type TexasAmData = GenericExternalGeoEventData & {
  externalSource: 'texas_am';
  category?: string | null;
  status: string;
};

export type WeaData = GenericExternalGeoEventData & {
  externalSource: 'wea';
  severity: string;
};

export type WeaPbsWarnData = GenericExternalGeoEventData & {
  externalSource: 'wea_pbs_warn';
  areaDescription: string;
  notes: string[];
  sender: string;
};

export type WiDnrData = GenericExternalGeoEventData & {
  externalSource: 'wi_dnr';
  fireStatus: string;
  generalCause: string;
  structureThreatened: string;
};

export type WildcadData = GenericExternalGeoEventData & {
  externalSource: 'wildcad';
  incidentNumber: string;
  incidentType: string;
  status: string;
};

type SpecificExternalGeoEventData =
  | AircraftDetectionData
  | AlertWestAiHitData
  | AlertWestDetectionData
  | ArkansasForestryData
  | CalFireData
  | ChpData
  | DnrData
  | FireGuardData
  | FiresponseData
  | FloridaForestServiceData
  | MfcData
  | N5ShieldData
  | NgfsData
  | NwsAlertData
  | NwsLsrData
  | OdfData
  | PfirsData
  | PulsePointData
  | RiverGaugeData
  | SpotForecastData
  | SynopticData
  | TexasAmData
  | WeaData
  | WeaPbsWarnData
  | WiDnrData
  | WildcadData;

// Some externalSources have more specific types defined above.
// Base covers every source that doesn't have a dedicated type; the
// exclude list is derived from the union so adding a new specific
// type updates Base automatically.
export type BaseExternalGeoEventData = GenericExternalGeoEventData & {
  externalSource: Exclude<
    ExternalSource,
    SpecificExternalGeoEventData['externalSource']
  >;
};

export type ExternalGeoEventData =
  | BaseExternalGeoEventData
  | SpecificExternalGeoEventData;

type GenericExternalGeoEvent = {
  id: number;
  lat: number;
  lng: number;
  dateCreated: string;
  dateModified: string;
  externalId: string;
  messageId: string;
  channel: string;
  permalinkUrl: string | null;
  requestedAcks: RequestedAck[];
  reasonNotProcessed: string | null;
  isApprovable: boolean;
};

export type Ack = {
  id: number;
  requestedAckId: number;
  userCreated: User;
};

export type RequestedAck = {
  id: number;
  acks: Ack[];
  dateCreated: string;
  dateModified: string;
  state: string;
  externalGeoEventId: number;
};

// One event type per data type, sharing the discriminant with its data.
// The full union is derived below so adding a new data type only
// requires adding it to SpecificExternalGeoEventData.
type SpecificExternalGeoEvent = SpecificExternalGeoEventData extends infer D
  ? D extends { externalSource: infer S }
    ? GenericExternalGeoEvent & { externalSource: S; data: D }
    : never
  : never;

export type BaseExternalGeoEvent = GenericExternalGeoEvent & {
  externalSource: BaseExternalGeoEventData['externalSource'];
  data: BaseExternalGeoEventData;
};

export type ExternalGeoEvent = BaseExternalGeoEvent | SpecificExternalGeoEvent;

export type PulsePointExternalGeoEvent = Extract<
  ExternalGeoEvent,
  { externalSource: 'pulsepoint' }
>;

export type AlertWestAiHitExternalGeoEvent = Extract<
  ExternalGeoEvent,
  { externalSource: 'alert_west' }
>;

export type FormEvacZoneStatus = {
  status: EvacZoneStatus;
  evacZone: GeoEventEvacZone;
  prevStatus?: EvacZoneStatus;
};

export type GeoEventFormStatus = 'active' | 'inactive';

export type EvacZoneStatusItem = {
  evacZone: { id: number };
  status: EvacZoneStatus;
};

export type LayerEvacZone = {
  uidV2: string;
  status?: EvacZoneStatus;
  style: RegionEvacZoneStyle;
  pendingDisplayName?: string | null;
};

export type IFrameProps = {
  src: string;
  containerWidth: number;
};

export type Topic = {
  id: number;
  key: string;
  type: string;
  incident: number;
  geoEvent: number;
  name: string;
  isActive: boolean;
  dateCreated: string;
  dateModified: string;
};

export type UserGroups =
  | 'reporter_admins'
  | 'reporters'
  | 'wildfire_staff_reporters'
  | 'wildfire_reporters'
  | 'flood_staff_reporters'
  | 'flood_reporters'
  | 'rx_reporters'
  | 'internal_users'
  | 'member_benefits'
  | 'synthetic_authorized_user'
  | 'synthetic_internal_user'
  | 'send_critical_alerts'
  | 'approvers';

export type UserGroup = {
  name: UserGroups;
};

export type Organization = {
  id: number;
  name: string;
  role: OrganizationRole;
};

export type UserSupporterStatus = {
  hasActiveMembership: boolean;
  isDonor: boolean;
  /** Set only while `hasActiveMembership` is true, so a lapsed member has none. */
  planType: UserPlanType | null;
  /**
   * Present for a lapsed membership too, so it is the only field that separates
   * "subscribed and lapsed" from "never subscribed". May be in the past.
   */
  dateActiveUntil: string | null;
  requiresOriginalTransactionId: boolean;
  isOrganizationUser: boolean;
  canManageOrganization: boolean;
};

export type UserPermissionContext = 'always';

export type UserPermissions = {
  canViewInternalLightningLayer?: UserPermissionContext;
  canViewFlightTrackerLayer?: UserPermissionContext;
};

export type UserPermissionKey = keyof UserPermissions;

export type ResolvedUserPermissions = Partial<
  Record<UserPermissionKey, boolean>
>;

export type User = {
  id: number;
  displayName: string;
  username: string;
  isVerified: boolean;
  groups?: UserGroup[]; // this was added in May 2022
  email: string;
  userprofile?: {
    bio: string | null;
    avatarUrl: string | null;
    headline: string | null;
  };
  organizations: Organization[]; // comes as an array - for now should always be 1
  firstName?: string;
  lastName?: string;
  supporterStatus?: UserSupporterStatus;
  permissions?: UserPermissions;
};

export type AuthState = {
  isAuthenticated: boolean;
  accessKey: string | null;
};

// Not entirely clear why it sometimes comes in as _ne and other times _northEast
export type SerializedBounds = {
  _ne?: {
    lat: number;
    lng: number;
  };
  _sw?: {
    lat: number;
    lng: number;
  };
  _northEast?: {
    lat: number;
    lng: number;
  };
  _southWest?: {
    lat: number;
    lng: number;
  };
};

export type MapSearchApi = 'mapbox' | 'google';

export type PositionError = Error | GeolocationPositionError;

export type PositionResult = {
  lat?: number;
  lng?: number;
  az?: number;
  accuracy?: number;
};

export type UserPositionResult = {
  locationEnabled: boolean;
  error: PositionError | null;
  position: PositionResult | null;
};

export type UserPosition = PositionResult & {
  error: PositionError | null;
  getUserPosition: () => Promise<UserPositionResult>;
  askForPermission: boolean;
  setAskForPermission: Dispatch<SetStateAction<boolean>>;
  locationEnabled: boolean;
};

export type MapLocation = {
  name: string;
  lat: number;
  lng: number;
  bbox?: number[];
  // @todo remove `place` when removing 'pin_drop_improvements' release flag
  place?: boolean;
  formattedAddress?: string;
  type?: 'Feature' | 'GeoEvent' | 'LatLngLocation';
  geoEventId?: number;
};

export type TileSetType = {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
};

export type AirCraftType =
  | 'drone'
  | 'fixedWing'
  | 'heloType1Blackhawk'
  | 'heloType1Chinook'
  | 'heloType1Skycrane'
  | 'heloType2Medium'
  | 'heloType3Light'
  | 'jumpShip'
  | 'tankerSeats'
  | 'tankerScooper'
  | 'tankerType1Lat'
  | 'tankerType1Vlat'
  | 'tankerType2'
  | 'tankerType3';

// see more at https://www.adsbexchange.com/version-2-api-wip/
export type AirCraft = {
  aircraftType: AirCraftType;
  hex: string;
  type: string;
  flight: string;
  r: string;
  t: string;
  dbFlags?: number;
  altBaro?: Altitude;
  altGeom?: number;
  gs: number;
  ias?: number;
  tas?: number;
  mach?: number;
  wd?: number;
  ws?: number;
  oat?: number;
  tat?: number;
  track?: number;
  trackRate?: number;
  roll?: number;
  magHeading?: number;
  trueHeading?: number;
  baroRate?: number;
  geomRate?: number;
  squawk?: string;
  emergency?: string;
  category?: string;
  navQnh?: number;
  navAltitudeMcp?: number;
  navHeading?: number;
  lat: number;
  lon: number;
  nic?: number;
  rc?: number;
  seenPos?: number;
  version?: number;
  nicBaro?: number;
  nacP?: number;
  nacV?: number;
  sil?: number;
  silType?: string;
  gva?: number;
  sda?: number;
  alert?: number;
  spi?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mlat?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tisb?: any[];
  messages?: number;
  seen?: number;
  rssi?: number;
};

export type Altitude = number | 'ground';

export type MapDrawerEntityType = 'privateLandOwnership' | 'mapPin';

export type PrivateLandOwnershipEntity = {
  uuid: string;
};

export type ProcessOrganizationPaymentTokenData = {
  companyName: string;
  email: string;
  authorizationToken?: string;
  firstName?: string;
  lastName?: string;
  clientToken?: string;
  verificationToken?: string;
  userId?: number;
  proSeats?: number;
  regularSeats?: number;
};

export type ProcessMembershipPaymentTokenData = {
  email: string;
  planCode: string;
  optInToUpdates: boolean;
  authorizationToken?: string;
  firstName?: string;
  lastName?: string;
  clientToken?: string;
  verificationToken?: string;
  userId?: number;
};

export type ProcessMembershipUpgradeData = {
  verificationToken: string;
  userId: number;
  upgradePlanCode: string;
  fromIap: boolean;
};

export type ProcessInitialDonationPaymentTokenData = {
  clientToken: string;
  donationAmount: number;
  donationRenews: boolean;
  firstName: string;
  lastName: string;
  email: string;
  optInToUpdates: boolean;
  authorizationToken: string;
};

export type ProcessDonationPaymentTokenData = {
  userId: number;
  verificationToken: string;
  donationAmount: number;
  donationRenews: boolean;
  authorizationToken?: string;
};

export type ProcessPaymentState = {
  companyName?: string;
  email: string;
  firstName: string;
  lastName: string;
  optInToUpdates: boolean;
  transactionAmount: number;
  clientToken?: string;
  existingUser?: boolean;
  donationRenews?: boolean;
  fromMobile?: boolean;
  verificationToken?: string;
  userId?: number;
  updatePaymentMethod?: boolean;
  proSeats?: number;
  regularSeats?: number;
};

type CheckoutSummaryConfig = {
  title: string;
  description: string;
};

export type DonationCheckoutConfig = CheckoutSummaryConfig;

export type MembershipCheckoutConfig = CheckoutSummaryConfig & {
  code: string;
};

export type CardType =
  | 'Visa'
  | 'MasterCard'
  | 'Amex'
  | 'Discover'
  | 'PayPal'
  | 'ApplePay'
  | 'American Express'
  | 'PayPalLogo';

export type PaymentMethodData =
  | {
      cardType: CardType;
      expMonth: number;
      expYear: number;
      lastFour: string;
      paymentType: 'credit_card';
    }
  | {
      paymentType: 'apple_pay' | 'paypal_billing_agreement';
    };

export type SavePaymentMethodData = {
  userId: number;
  verificationToken: string;
  authorizationToken: string;
};

export type VerifyPaymentData = {
  key: string;
  user: User;
};

export type PaywallFeature =
  | 'flightTracker'
  | 'notifications'
  | 'places'
  | 'geoEventInfo';

export type PaginatedAPIResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type UploadImageData = {
  media: File | Blob;
  s3AuthData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: { url: string; fields: Record<string, any> };
  };
  s3Key: string;
};

export type GPSData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bearing: any;
  lat: number;
  lng: number;
};

export type LatLng = {
  lat: number;
  lng: number;
};

export type ReportAsset = 'image' | 'socialEmbed' | 'alertCamera' | null;

export type QueryApiResult<T> = {
  data: T;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
};

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export type Membership = 'public' | UserPlanType;

export type MembershipSource = 'ios' | 'recurly';

export type FormFieldProps<TName extends FieldPath<FieldValues>> = {
  field: ControllerRenderProps<FieldValues, TName>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FieldValues>;
};

export type S3AuthData = {
  url: string;
  fields: {
    key: string;
  };
};

/**
 * TS v5.5 deprecated suppressImplicitAnyIndexErrors.
 * This typeguard can help us work around the
 * "can't be used to index" errors it suppressed by
 * preceding attempts to access an object:
 * (isKeyInObject(key, obj) && obj[key]).
 *
 * This is usually only needed when objects are typed
 * as anything other than Record<string, whatever>.
 */
export const isKeyInObject = <T extends object>(
  key: number | symbol | string,
  obj: T,
): key is keyof T => {
  return key in obj;
};

export type NullableObject<T> = {
  [K in keyof T]: T[K] | null;
};

export type WithStringDates<T> = Omit<T, 'dateCreated' | 'dateModified'> & {
  dateCreated: string;
  dateModified: string;
};
